
import React, { useState, useEffect, useContext, useRef } from 'react';
import { withAuth } from '@cdk-prod/fortellis-auth-context';
import { APIDetails } from '@cdk-prod/api-directory-component';
import { Page } from '@fortellis/page';
import { Link, useParams, useHistory, useLocation } from 'react-router-dom';
import {
	API_DIRECTORY_THEME,
	API_DETAILS_HANDLERS_MODEL,
	APP_STATUS
} from '../../utils/constants';
import config from '../../config/config';
import axios from 'axios';
import { useUserProfile } from '../../hooks/use-query';
import FortellisBot from '../common/FortellisBot';
import { sendAmplitudeData } from '../../utils/amplitude';
import {
	getOrGenerateIdentifierId,
	getOrgUnitId,
	normalizeGtmUserRoles
} from '../../utils/gtmUtils';

const buildWithTypes = {
  NEW_APP: 'newApp',
  EDIT_APP: 'editApp'
};

const solutionUrl = config.api.solutionUrl;

const capitalize = (str = '') =>
	str && str.length >= 2 && str.charAt(1) !== str.charAt(1).toUpperCase()
		? str.charAt(0).toUpperCase() + str.slice(1)
		: str;

const BundleDetailsWrapper = ({
	store,
	auth,
	dispatch,
	entityContext: { entity, loadingEntity, entities } = {},
	fetchApiSpecInBundle,
}) => {
	const params = useParams();
	const bundleId = params?.id;
	const history = useHistory();
	const location = useLocation();
	const lastPushedEvent = useRef(null);

	const [appDataLoadStarted, setAppDataLoadStarted] = useState(false);
	const [appDataLoading, setAppDataLoading] = useState(true);
	const [appData, setAppData] = useState([]);

	const { apiInfo, bundleDetails, apiDetailsInBundle, apiSpecInBundle } = store;
	const bundleData = bundleDetails?.data || apiInfo?.data;
	const { roles: userRoles, isLoading: isProfileLoading } = useUserProfile(auth);

	const {
		isAuthenticated,
		userData: { name: userName, email: userEmail } = {}
	} = auth;
	const { id: entityId, name: entityName } = entity || {};

	const authProp = {
		...auth,
		isAuthenticated,
		userData: {
			name: userName,
			email: userEmail
		},
		orgData: {
			id: entityId,
			name: entityName
		}
	};

	useEffect(() => {
		if (auth?.accessToken && !appDataLoadStarted) {
			setAppDataLoadStarted(true);
			axios({
				method: 'GET',
				url: `${solutionUrl}/v1/solutions/?includeTest=false`,
				headers: {
					Authorization: `Bearer ${auth?.accessToken}`
				}
			})
				.then(response => {
					const appData = response?.data
						?.filter(app => app?.status !== APP_STATUS.CANCELLED)
						.map(app => ({
							displayName: app?.displayName,
							id: app?.id,
							disabledFeatures: app?.disabledFeatures || []
						}));
					setAppData(appData);
					setAppDataLoading(false);
				})
				.catch(() => {
					console.error('failed to get app details');
					setAppDataLoading(false);
				});
		} else if (auth?.loginPending === false && !auth?.isAuthenticated) {
			setAppDataLoading(false);
		}
	}, [
		auth?.accessToken,
		auth?.loginPending,
		auth?.isAuthenticated,
		appDataLoadStarted
	]);

	useEffect(() => {
		// Wait for auth to be fully initialized
		if (
			typeof auth?.isAuthenticated !== 'boolean' ||
			auth?.loginPending
		) {
			return;
		}

		// Push pageview when bundle is loaded.
		if (!bundleDetails?.isFetching && bundleData && !bundleDetails?.error) {
			if (auth?.isAuthenticated) {
				const isEntityLoading =
					loadingEntity === undefined || loadingEntity === true;
				const isMissingExpectedOrgDetails = auth?.entityId && !entity?.name;
				if (isEntityLoading || isMissingExpectedOrgDetails || isProfileLoading) return;
			}

			const currentEntity = entity || {};
			const fortellisId = auth?.isAuthenticated
				? currentEntity.id || auth?.entityId || null
				: null;
			const orgName = auth?.isAuthenticated ? currentEntity.name || null : null;
			const rolesPayload = auth?.isAuthenticated
				? normalizeGtmUserRoles(userRoles)
				: null;
			const userId = auth?.isAuthenticated
				? auth?.userData?.sub || null
				: null;
			const identifierId = getOrGenerateIdentifierId();
			const orgUnitId = auth?.isAuthenticated
				? getOrgUnitId(currentEntity, entities, fortellisId)
				: null;
			const storeId = orgUnitId || null;
			const storeName = orgName || null;

			const apiIdsList = Array.isArray(bundleData?.apis)
				? bundleData.apis
						.map(api => (typeof api === 'string' ? api : api?.id))
						.filter(Boolean)
				: [];
			const apiIds = apiIdsList.length > 0 ? apiIdsList : null;

			const currentEventKey = `${location.pathname}-${userId || ''}-${fortellisId || ''}-${bundleId || ''}-${
				rolesPayload ? rolesPayload.join(',') : ''
			}`;
			if (lastPushedEvent.current !== currentEventKey) {
				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: 'fortellis-pageview',
					page_section: 'API Directory',
					page_subsection: 'Bundle Details',
					directory_type: 'ISV',
					bundle_id: bundleId,
					bundle_name: bundleData?.name || null,
					api_ids: apiIds,
					identifier_id: identifierId,
					fortellis_user_id: userId,
					user_id: null,
					enterprise_id: null,
					enterprise_name: null,
					company_id: null,
					store_id: storeId,
					store_name: storeName,
					stores: null,
					product_features: null,
					user_org: orgName,
					user_role: rolesPayload,
					page_url: window.location.href || null,
					page_path: location.pathname || null,
					is_impersonated_session: false,
					fortellis_id: fortellisId,
					fortellis_org_name: orgName,
					product: 'API',
					application: 'Fortellis Network'
				});

				lastPushedEvent.current = currentEventKey;
			}
		}
	}, [
		bundleDetails,
		bundleData,
		auth,
		entity,
		loadingEntity,
		entities,
		bundleId,
		location.pathname,
		userRoles,
		isProfileLoading
	]);

	const getBreadcrumbs = bundleName => {
		const urlParams = new URLSearchParams(window.location.search);
		const storedFilters = sessionStorage.getItem('selectedFilters');
		const hasActiveFilters = urlParams.toString() || storedFilters;
		const apiDirectoryUrl = hasActiveFilters
			? `/?${storedFilters || urlParams.toString()}`
			: '/';

		return [{ text: 'API Directory', url: apiDirectoryUrl }, { text: bundleName }];
	};

    const buildWithBundle = ({ appId, buildWithType, bundleId }) => {
      if (buildWithType === buildWithTypes.NEW_APP) {
        window.open(
          `${config.developer.url}developer-account/solutions/register?bundleId=${bundleId}`
        );
      } else {
        window.open(
          `${config.developer.url}developer-account/solutions/edit/${appId}?bundleId=${bundleId}`
        );
      }
    };

	const apiDetailsHandlers = {
		...API_DETAILS_HANDLERS_MODEL,
        buildWithBundle,
	};

	const apiHomeOnClickHandlers = {
		fetchApiSpec: fetchApiSpecInBundle
	};

	const title = bundleData?.name || '';

	if (bundleDetails?.error) {
		return <div className="error-page error-title">{bundleDetails.error}</div>;
	}

	// This env object is used by the component for docs/spec experiences.
	const envURLs = { DOCS_UI_URL: `${config.apidocs.url}` };

	return (
		<>
			{!bundleDetails?.isFetching && bundleData && (
				<Page
					breadcrumbs={getBreadcrumbs(capitalize(title))}
					routerLink={Link}
				/>
			)}
			<APIDetails
				apiId={bundleId}
				apiInfo={apiInfo}
				entity={entity}
				bundleDetails={bundleDetails?.data}
				bundleDetailsViewEnabled={true}
				fetchingBundleDetails={bundleDetails?.isFetching}
				fetchingAPISpecInBundle={apiSpecInBundle?.isFetching}
				errorInFetchingAPISpecInBundle={apiSpecInBundle?.error}
				apiDetailsOnClickHandlers={apiDetailsHandlers}
				apiHomeOnClickHandlers={apiHomeOnClickHandlers}
				cartDetails={[]}
				orderDetails={[]}
				auth={authProp}
				belongsTo={API_DIRECTORY_THEME.ISV_API_DIRECTORY}
				sendAmplitudeData={sendAmplitudeData}
				environmentURLs={envURLs}
				requestingOrg={auth?.entityId}
				existingSolutionDetails={appData}
				disableBuildNow={appDataLoading}
			/>
			<FortellisBot auth={auth} entity={entity} />
		</>
	);
};

export default withAuth(BundleDetailsWrapper);

